import type { Hive } from '../ScopingEditor/useGetState';
import { SVGProps, memo } from 'react';
import { AutoTooltip } from '@/components';
import { HiveIcon } from '@/components/Icons';
import { useZoom } from '../ZoomContext';
import { HiveConnectionHealthStatus } from '@/pages/DeviceManagementPage/HiveTable';
import { useAppSelector } from '@/redux/hooks';
import { selectIsHighlighted } from '../ScopingEditor/reducer';
import { HIVE_MODEL_SHORT_NAME } from '@/constants';
import HiveConnectionRadius from './HiveConnectionRadius';
import { useCanvasColors } from './canvasColors';
export const HIVE_ICON_SIZE = 1.25;

function HiveModelLabel({
  zoom,
  modelLabel,
  labelBg,
}: {
  zoom: number;
  modelLabel: string;
  labelBg?: string;
}) {
  const shortLabel = HIVE_MODEL_SHORT_NAME[modelLabel] || modelLabel;
  return (
    <g transform={`translate(${0.8 / zoom}, ${-1.2 / zoom})`}>
      <rect
        className="fill-gray-100"
        style={labelBg ? { fill: labelBg } : undefined}
        x={0}
        y={-0.6 / zoom}
        width={2.4 / zoom}
        height={1.2 / zoom}
        rx={0.2 / zoom}
        ry={0.2 / zoom}
      />
      <text
        className="font-semibold"
        x={1.2 / zoom}
        y={0}
        textAnchor="middle"
        alignmentBaseline="middle"
        fontSize={0.6 / zoom}
      >
        {shortLabel}
      </text>
    </g>
  );
}

function HiveItem({
  x = 0,
  y = 0,
  name,
  selected,
  id,
  locked,
  selecting = false,
  fade = false,
  onClick,
  serial_number,
  connectionHealth,
  isSleepTime,
  model,
  showHiveModel,
  isArchived,
  isClone,
  ...rest
}: {
  selected: boolean;
  serial_number: string;
  locked?: boolean;
  selecting?: boolean;
  fade?: boolean;
  connectionHealth?: { status: HiveConnectionHealthStatus };
  isSleepTime?: boolean;
  id: string;
  model?: string;
  showHiveModel?: boolean;
  isArchived?: boolean;
  isClone?: boolean;
} & SVGProps<SVGGElement> &
  Pick<Hive, 'name'>) {
  const { adjustedZoom, zoom } = useZoom();
  const colors = useCanvasColors();
  const modelLabelZoom = Math.max(zoom, 3.5);
  const isPlaceholder = !serial_number;
  const color = (() => {
    if (isArchived) return '#24252C';
    if (isPlaceholder) return '#8F91A3';

    const status = connectionHealth?.status;
    if (status === 'CONNECTED' || isSleepTime) return '#10B981';
    if (status === 'PARTIALLY_CONNECTED') return '#EB860A';
    if (status === 'NOT_CONNECTED') return '#FF0000';
    return '#24252C';
  })();

  function getHiveLabel(
    isClone: boolean | undefined,
    name?: string,
    isArchived?: boolean,
  ): string | undefined {
    if (isArchived) {
      const baseLabel = isPlaceholder ? name ?? '' : serial_number?.slice(-6) ?? '';
      return `${baseLabel} (Archived)`;
    }
    if (isClone) {
      const sn = serial_number?.replace(/^fake/, '') || 'N/A';
      return `V-${sn}`;
    }
    return isPlaceholder ? name : serial_number?.slice(-6) ?? serial_number;
  }

  const isHighlighted = useAppSelector((state) => selectIsHighlighted(state, id));
  const showAsSelected = selected || selecting;
  // stroke is dashed for highlighted, non-selected items
  const outlineIsDashed = !selected && !selecting && isHighlighted;

  return (
    <g
      data-id="hive-item"
      id={id}
      onClick={onClick}
      transform={`translate(${x}, ${y})`}
      style={{ pointerEvents: locked ? 'none' : 'all', opacity: fade ? 0.5 : 1 }}
      data-highlighted={isHighlighted}
      data-selected={selected}
      {...rest}
    >
      {(showAsSelected || isHighlighted) && <HiveConnectionRadius />}
      <AutoTooltip
        label={getHiveLabel(isClone, name, isArchived)}
        variant="white"
        placement="top"
        keepOpenOnMouseOver
        forceOpen={selected || isHighlighted}
        autoUpdateOnAnimationFrame
      >
        {(props) => (
          <g transform="scale(1, -1)" {...props}>
            <rect
              x={-HIVE_ICON_SIZE / 2 / adjustedZoom}
              y={-HIVE_ICON_SIZE / 2 / adjustedZoom}
              width={HIVE_ICON_SIZE / adjustedZoom}
              height={HIVE_ICON_SIZE / adjustedZoom}
              stroke={color}
              strokeWidth={(showAsSelected ? 0.1 : 0.05) / adjustedZoom}
              strokeOpacity={0.8}
              strokeDasharray={outlineIsDashed ? 0.05 : 0}
              fill={colors.hiveIconBg}
              rx={HIVE_ICON_SIZE / 10 / adjustedZoom}
            />
            <HiveIcon
              className="pointer-events-none"
              color={color}
              height={HIVE_ICON_SIZE / 1.5 / adjustedZoom}
              width={HIVE_ICON_SIZE / 1.5 / adjustedZoom}
              x={-HIVE_ICON_SIZE / 1.5 / 2 / adjustedZoom}
              y={-HIVE_ICON_SIZE / 1.5 / 2 / adjustedZoom}
            />
            {showHiveModel && model && !isPlaceholder && (
              <HiveModelLabel
                zoom={modelLabelZoom}
                modelLabel={model}
                labelBg={colors.sensorLabelBg}
              />
            )}
          </g>
        )}
      </AutoTooltip>
    </g>
  );
}

export default memo(HiveItem);
